import { Link, useLocation } from 'react-router-dom';
import { usePermissions } from '@/api/permissions';
import { canOpen } from '@/lib/permissions';
import { Building2 } from 'lucide-react';
import { useCompanies } from '@/api/insights';
import { useClientScope } from '@/contexts/ClientScopeContext';
import {
  ROUTES, backupSaasCustomerPath, cippTenantPath, companyPath, docsOrgPath, rmmClientSitesPath,
} from '@/lib/routes';
import { cn } from '@/lib/utils';

/**
 * "Client mode" (general.md §4.9): while a client is picked in the header, the top of the sidebar
 * lists that client's pages — Orbit first, then one link per product it's actually in. Where
 * the client has a single record (M365 tenant, backup customer, IT Glue org) the link goes straight
 * to it; otherwise to the list, which is already narrowed to the client.
 */
export function ClientModeSection({ rail = false, onNavigate }: { rail?: boolean; onNavigate?: () => void }) {
  const { scope } = useClientScope();
  const { pathname } = useLocation();
  const companies = useCompanies({ enabled: !!scope });
  const perms = usePermissions();
  if (!scope) return null;

  const c = companies.data?.companies.find((x) => x.id === scope.companyId);
  const links: { to: string; label: string }[] = [{ to: companyPath(scope.companyId), label: 'Orbit' }];
  links.push({ to: ROUTES.psa.tickets.queues, label: 'Unassigned tickets' });
  if (c?.rmm) links.push({ to: rmmClientSitesPath(scope.companyId), label: `Devices (${c.rmm.devices})` });
  if (c?.cipp) {
    links.push({ to: cippTenantPath(c.cipp.customerId), label: 'Microsoft 365 users' });
    links.push({ to: ROUTES.cipp.devices, label: 'Intune devices' });
  }
  if (c?.backup?.length) links.push({ to: c.backup.length === 1 ? backupSaasCustomerPath(c.backup[0].saasCustomerId) : ROUTES.backup.saas, label: 'SaaS backup' });
  if (c?.network) links.push({ to: ROUTES.network.root, label: 'Network' });
  if (c?.domains) links.push({ to: ROUTES.domains.root, label: 'Domains' });
  if (c?.docs) links.push({ to: docsOrgPath(c.docs.orgId), label: 'IT Glue' });
  if (c?.soc) links.push({ to: ROUTES.soc.root, label: 'SOC (Huntress)' });
  if (c?.csp) links.push({ to: ROUTES.csp.licences, label: 'CSP licences' });
  links.push({ to: ROUTES.insights.gaps, label: 'Gaps' });
  // RBAC: only what the role can open.
  const shown = links.filter((l) => canOpen(perms, l.to));
  const orbit = canOpen(perms, companyPath(scope.companyId));

  if (rail) {
    if (!orbit) return null;
    return (
      <Link to={companyPath(scope.companyId)} title={`${scope.name}: Orbit`} aria-label={`${scope.name}: Orbit`}
        className="flex h-9 w-9 items-center justify-center rounded-md bg-brand-soft text-brand-strong dark:text-brand">
        <Building2 className="h-4 w-4" />
      </Link>
    );
  }

  return (
    <div className="mb-2 rounded-lg border border-brand/30 bg-brand-soft/50 px-1.5 py-1.5">
      <div className="flex items-center gap-2 px-2 py-1 text-[15px] font-medium text-brand-strong dark:text-brand">
        <Building2 className="h-4 w-4 shrink-0" />
        <span className="truncate" title={scope.name}>{scope.name}</span>
      </div>
      {shown.map((l) => (
        <Link key={l.to + l.label} to={l.to} onClick={onNavigate}
          className={cn('block truncate rounded-md py-1.5 pl-8 pr-2 text-sm transition-colors',
            pathname === l.to.split('?')[0] ? 'bg-background/70 font-medium text-foreground' : 'text-muted-foreground hover:bg-background/60 hover:text-foreground')}>
          {l.label}
        </Link>
      ))}
    </div>
  );
}
